import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, CheckCircle, ShareNetwork } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useBrand } from "@/lib/useBrand";

export default function ShareDialog({ open, onOpenChange, url }) {
  const [copied, setCopied] = useState(false);
  const { name: appName } = useBrand();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for non-https / iframe blocked
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        toast.success("Link copied");
        setTimeout(() => setCopied(false), 2000);
      } catch {
        toast.error("Copy failed — long-press the URL to copy");
      }
      document.body.removeChild(ta);
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "My Irresistible Offer",
          text: `Forged with ${appName}`,
          url,
        });
      } catch {
        // user cancelled
      }
    } else {
      copy();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0F1F38] border-white/10 text-[#F5F1E8] max-w-md"
        data-testid="share-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold tracking-tight">
            Share this Irresistible Offer
          </DialogTitle>
          <DialogDescription className="text-[#9AA5B5]">
            Anyone with the link can view the offer (read-only).
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 bg-[#0A1628] border border-white/10 rounded-sm p-3 flex items-center gap-2">
          <input
            readOnly
            value={url}
            onFocus={(e) => e.target.select()}
            className="flex-1 bg-transparent text-sm text-[#F5F1E8] font-mono outline-none"
            data-testid="share-dialog-url"
          />
          <Button
            onClick={copy}
            size="sm"
            className="bg-[#C9A961] text-[#0A1628] font-bold hover:bg-[#E0B64B] rounded-sm"
            data-testid="share-dialog-copy-btn"
          >
            {copied ? (
              <>
                <CheckCircle weight="fill" className="mr-1 h-4 w-4" /> Copied
              </>
            ) : (
              <>
                <Copy weight="bold" className="mr-1 h-4 w-4" /> Copy
              </>
            )}
          </Button>
        </div>

        <div className="flex items-center gap-3 mt-2">
          <Button
            onClick={nativeShare}
            variant="outline"
            className="flex-1 bg-transparent border-white/15 text-[#F5F1E8] hover:bg-white/5 hover:border-white/30 rounded-sm"
            data-testid="share-dialog-native-btn"
          >
            <ShareNetwork weight="bold" className="mr-2 h-4 w-4" /> Share via…
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
